import { useTheme } from "../../styles/theme";
import { useApp } from "../../context/AppContext";
import { dispDate, daysBetween } from "../../utils/dates";
import { fmt } from "../../utils/format";
import SectionTitle from "../../components/SectionTitle";
import StatCard from "../../components/StatCard";
import FlowRow from "../../components/FlowRow";
import { useNavigate } from "react-router-dom";
import * as api from "../../services/api";

export default function Dashboard() {
  const S = useTheme();
  const {
    data,
    form,
    setForm,
    save,
    showToast,
    todaySummary,
    dailySummaries,
    pendingVendor,
    overdueDebts,
    markVendorPaid,
    TODAY,
  } = useApp();

  const navigate = useNavigate();

  const todayOB = todaySummary.openingBalance;
  const todayEOD = todaySummary.eodBalance;
  const todayEODifAllPaid = todaySummary.eodIfAllPaid;

  return (
    <div>
      {/* Opening Balance Setup Banner */}
      {!(data.openingBalances || []).find((b) => b.date === TODAY) && (
        <div style={S.obBanner}>
          <div style={S.obBannerLeft}>
            <span style={{ fontSize: 22 }}>🏦</span>
            <div>
              <div
                style={{ fontWeight: 700, color: S.colors.text, fontSize: 15 }}
              >
                Set Today's Opening Balance
              </div>
              <div style={{ fontSize: 12, color: S.colors.textDim, marginTop: 2 }}>
                How much cash do you have in hand right now at start of business
                today?
                {dailySummaries.length > 0 &&
                  ` (Auto-calculated from yesterday: ${fmt(
                    dailySummaries[dailySummaries.length - 1].eodBalance,
                  )})`}
              </div>
            </div>
          </div>
          <div style={S.obBannerRight}>
            <input
              style={{ ...S.input, width: 150 }}
              type="number"
              placeholder={
                dailySummaries.length > 0
                  ? String(
                      Math.round(
                        dailySummaries[dailySummaries.length - 1].eodBalance,
                      ),
                    )
                  : "e.g. 10000"
              }
              value={form.quickOB || ""}
              onChange={(e) => setForm({ ...form, quickOB: e.target.value })}
            />
            <button
              style={S.obBannerBtn}
              onClick={async () => {
                if (!form.quickOB) {
                  showToast("Enter amount", "error");
                  return;
                }
                try {
                  await api.openingBalances.upsert(
                    TODAY,
                    parseFloat(form.quickOB),
                    "Daily opening",
                  );
                  const existing = (data.openingBalances || []).filter(
                    (b) => b.date !== TODAY,
                  );
                  save({
                    ...data,
                    openingBalances: [
                      ...existing,
                      {
                        date: TODAY,
                        amount: parseFloat(form.quickOB),
                        note: "Daily opening",
                      },
                    ],
                  });
                  setForm({});
                  showToast(`Opening balance set to ${fmt(parseFloat(form.quickOB))}`);
                } catch (err) {
                  showToast(err.message, "error");
                }
              }}
            >
              Set Balance
            </button>
          </div>
        </div>
      )}

      {/* Today's Cash Flow Panel */}
      <SectionTitle>Today's Cash Flow — {dispDate(TODAY)}</SectionTitle>
      <div style={S.cashFlowPanel}>
        {/* Left: Flow breakdown */}
        <div style={S.cashFlowLeft}>
          <FlowRow
            icon="🏦"
            label="Opening Balance"
            value={fmt(todayOB)}
            color={S.colors.info}
            sub={todaySummary.manualOB ? "manually set" : "from yesterday EOD"}
          />
          <div style={S.flowDivider} />
          <FlowRow
            icon="💰"
            label="Sales (Cash In)"
            value={`+ ${fmt(todaySummary.salesIn)}`}
            color={S.colors.primary}
          />
          <div style={S.flowDivider} />
          <FlowRow
            icon="💸"
            label="Direct Expenses"
            value={`− ${fmt(todaySummary.expensesOut)}`}
            color={S.colors.purple}
          />
          <FlowRow
            icon="🏪"
            label="Vendor Payments (paid)"
            value={`− ${fmt(todaySummary.vendorPaidOut)}`}
            color={S.colors.warn}
          />
          <FlowRow
            icon="👤"
            label="Salaries Paid"
            value={`− ${fmt(todaySummary.salariesOut)}`}
            color={S.colors.indigo}
          />
          {todaySummary.debtPaidOut > 0 && (
            <FlowRow
              icon="🔴"
              label="Debt Repayments"
              value={`− ${fmt(todaySummary.debtPaidOut)}`}
              color={S.colors.dangerLight}
            />
          )}
          <div style={S.flowDivider} />
          <FlowRow
            icon={todaySummary.netPL >= 0 ? "📈" : "📉"}
            label="Today's Net P&L"
            value={
              (todaySummary.netPL >= 0 ? "+ " : "− ") +
              fmt(Math.abs(todaySummary.netPL))
            }
            color={todaySummary.netPL >= 0 ? S.colors.primary : S.colors.danger}
          />
        </div>

        {/* Right: EOD summary */}
        <div style={S.cashFlowRight}>
          <div style={S.eodBox}>
            <div style={S.eodLabel}>💵 EOD Cash (if vendor paid)</div>
            <div
              style={{
                ...S.eodValue,
                color: todayEOD >= 0 ? S.colors.primary : S.colors.danger,
              }}
            >
              {fmt(todayEOD)}
            </div>
            <div style={S.eodFormula}>
              {fmt(todayOB)} + {fmt(todaySummary.salesIn)} −{" "}
              {fmt(
                todaySummary.expensesOut +
                  todaySummary.salariesOut +
                  todaySummary.vendorPaidOut +
                  todaySummary.debtPaidOut,
              )}
            </div>
          </div>

          {todaySummary.vendorDuePending > 0 && (
            <div style={S.eodPendingBox}>
              <div style={S.eodLabel}>⏳ EOD if pending vendor also paid</div>
              <div
                style={{
                  ...S.eodValue,
                  fontSize: 20,
                  color: todayEODifAllPaid >= 0 ? S.colors.warn : S.colors.danger,
                }}
              >
                {fmt(todayEODifAllPaid)}
              </div>
              <div style={S.eodFormula}>
                Pending vendor: {fmt(todaySummary.vendorDuePending)}
              </div>
            </div>
          )}

          <div style={S.eodStatusBox}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>
              {todayEOD > todayOB ? "📈" : todayEOD === todayOB ? "➡️" : "📉"}
            </div>
            <div
              style={{
                fontWeight: 700,
                color:
                  todayEOD > todayOB
                    ? S.colors.primary
                    : todayEOD === todayOB
                      ? S.colors.textDim
                      : S.colors.danger,
                fontSize: 14,
              }}
            >
              {todayEOD > todayOB
                ? `Up ${fmt(todayEOD - todayOB)} from opening`
                : todayEOD === todayOB
                  ? "No change from opening"
                  : `Down ${fmt(todayOB - todayEOD)} from opening`}
            </div>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <SectionTitle style={{ marginTop: 32 }}>Today's Breakdown</SectionTitle>
      <div style={S.cardGrid}>
        <StatCard
          label="Opening Balance"
          value={fmt(todayOB)}
          color={S.colors.info}
          icon="🏦"
          sub={todaySummary.manualOB ? "manually set" : "auto from yesterday"}
        />
        <StatCard
          label="Revenue Today"
          value={fmt(todaySummary.salesIn)}
          color={S.colors.primary}
          icon="💰"
        />
        <StatCard
          label="Total Expenses Today"
          value={fmt(
            todaySummary.expensesOut +
              todaySummary.salariesOut +
              todaySummary.vendorPaidOut,
          )}
          color={S.colors.dangerLight}
          icon="💸"
        />
        <StatCard
          label="Vendor Due (unpaid)"
          value={fmt(todaySummary.vendorDuePending)}
          color={S.colors.warn}
          icon="⏳"
        />
        <StatCard
          label="Net P&L Today"
          value={fmt(todaySummary.netPL)}
          color={todaySummary.netPL >= 0 ? S.colors.primary : S.colors.danger}
          icon={todaySummary.netPL >= 0 ? "📈" : "📉"}
        />
        <StatCard
          label="You Should Have EOD"
          value={fmt(todayEOD)}
          color={todayEOD >= 0 ? S.colors.primary : S.colors.danger}
          icon="💵"
          big
          sub={
            todaySummary.vendorDuePending > 0
              ? `Or ${fmt(todayEODifAllPaid)} if all vendors paid`
              : "All vendors paid"
          }
        />
      </div>

      {/* Alerts */}
      {overdueDebts.length > 0 && (
        <div style={{ ...S.alertBox, marginTop: 24 }}>
          <div style={S.alertTitle}>🔴 Overdue Debts — Pay Immediately!</div>
          {overdueDebts.map((d) => (
            <div key={d.id} style={S.alertRow}>
              <span>
                <strong>{d.vendor}</strong>
                {d.note ? ` — ${d.note}` : ""}
              </span>
              <span style={S.alertMeta}>
                Overdue {daysBetween(d.dueDate, TODAY)}d · {fmt(d.remaining)}
              </span>
              <button
                style={S.payBtn}
                onClick={() => navigate("/debt-manager")}
              >
                Manage →
              </button>
            </div>
          ))}
        </div>
      )}
      {pendingVendor.length > 0 && (
        <div
          style={{
            ...S.alertBox,
            marginTop: 12,
            borderColor: S.colors.warnBorder,
            background: S.colors.warnBg,
          }}
        >
          <div style={{ ...S.alertTitle, color: S.colors.warn }}>
            ⏳ Pending Vendor Payments
          </div>
          {pendingVendor.map((p) => (
            <div
              key={p.id}
              style={{ ...S.alertRow, borderTopColor: S.colors.warnBorder }}
            >
              <span>
                <strong>{p.vendor}</strong> — {p.item || "Purchase"} on{" "}
                {dispDate(p.purchaseDate)}
              </span>
              <span style={S.alertMeta}>
                Due: {dispDate(p.payDue)} · {fmt(p.amount)}
              </span>
              <button style={S.payBtn} onClick={() => markVendorPaid(p.id)}>
                Mark Paid
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
